import { supabase } from './supabaseClient';
import { sendEmail } from './emailService';

// Types per servizi
export interface ServiceProvider {
  id: string;
  name: string;
  email: string;
  phone?: string;
  whatsapp?: string;
  specialization: string[];
  bio_it?: string;
  bio_en?: string;
  profile_image?: string;
  calendar_url?: string;
  availability_hours?: any;
  hourly_rate?: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Service {
  id: string;
  provider_id: string;
  title_it: string;
  title_en: string;
  description_it?: string;
  description_en?: string;
  category: string;
  duration_minutes?: number;
  max_participants: number;
  price_per_person?: number;
  price_per_group?: number;
  images?: string[];
  location?: string;
  requirements?: string;
  cancellation_policy?: string;
  status: 'active' | 'inactive' | 'draft';
  created_at: string;
  updated_at: string;
  provider?: ServiceProvider;
}

export interface ServiceRequest {
  id: string;
  service_id: string;
  provider_id: string;
  client_name: string;
  client_email: string;
  client_phone?: string;
  requested_date?: string;
  requested_time?: string;
  preferred_time_slots?: string[];
  participants_count: number;
  message?: string;
  status: 'pending' | 'contacted' | 'confirmed' | 'cancelled' | 'completed';
  admin_notes?: string;
  provider_notes?: string;
  whatsapp_sent: boolean;
  email_sent: boolean;
  response_sent_at?: string;
  created_at: string;
  updated_at: string;
  service?: Service;
  provider?: ServiceProvider;
}

// ===== FUNZIONI SERVIZI =====

// Ottieni tutti i servizi attivi
export const getActiveServices = async (category?: string): Promise<{ 
  success: boolean; 
  services?: Service[]; 
  error?: string 
}> => {
  try {
    let query = supabase
      .from('services')
      .select(`
        *,
        provider:provider_id (
          id,
          name,
          email,
          phone,
          whatsapp,
          specialization,
          bio_it,
          bio_en,
          profile_image,
          hourly_rate
        )
      `)
      .eq('status', 'active');

    if (category) {
      query = query.eq('category', category);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;

    return { success: true, services: data || [] };
  } catch (error) {
    console.error('Errore recupero servizi:', error);
    return { success: false, error: (error as Error).message };
  }
};

// Ottieni servizio per ID
export const getServiceById = async (serviceId: string): Promise<{ 
  success: boolean; 
  service?: Service; 
  error?: string 
}> => {
  try {
    const { data, error } = await supabase
      .from('services')
      .select(`
        *,
        provider:provider_id (
          id,
          name,
          email,
          phone,
          whatsapp,
          specialization,
          bio_it,
          bio_en,
          profile_image,
          calendar_url,
          hourly_rate
        )
      `)
      .eq('id', serviceId)
      .eq('status', 'active')
      .single();

    if (error) throw error;

    return { success: true, service: data };
  } catch (error) {
    console.error('Errore recupero servizio:', error);
    return { success: false, error: (error as Error).message };
  }
};

// ===== FUNZIONI PROVIDER =====

// Ottieni tutti i provider attivi
export const getActiveProviders = async (): Promise<{ 
  success: boolean; 
  providers?: ServiceProvider[]; 
  error?: string 
}> => {
  try {
    const { data, error } = await supabase
      .from('service_providers')
      .select('*')
      .eq('active', true)
      .order('name');

    if (error) throw error;

    return { success: true, providers: data || [] };
  } catch (error) {
    console.error('Errore recupero provider:', error);
    return { success: false, error: (error as Error).message };
  }
};

// ===== FUNZIONI RICHIESTE SERVIZI =====

// Crea richiesta servizio
export const createServiceRequest = async (requestData: {
  serviceId: string;
  clientName: string;
  clientEmail: string;
  clientPhone?: string;
  requestedDate?: string;
  requestedTime?: string;
  preferredTimeSlots?: string[];
  participantsCount: number;
  message?: string;
}): Promise<{ success: boolean; request?: ServiceRequest; error?: string }> => {
  try {
    // Prima recupera info del servizio e provider
    const serviceResult = await getServiceById(requestData.serviceId);
    if (!serviceResult.success || !serviceResult.service) {
      throw new Error('Servizio non trovato');
    }

    const service = serviceResult.service;
    const provider = service.provider!;

    // Crea richiesta
    const { data: request, error } = await supabase
      .from('service_requests')
      .insert({
        service_id: requestData.serviceId,
        provider_id: service.provider_id,
        client_name: requestData.clientName,
        client_email: requestData.clientEmail,
        client_phone: requestData.clientPhone,
        requested_date: requestData.requestedDate,
        requested_time: requestData.requestedTime,
        preferred_time_slots: requestData.preferredTimeSlots,
        participants_count: requestData.participantsCount,
        message: requestData.message,
        status: 'pending',
        email_sent: false,
        whatsapp_sent: false
      })
      .select()
      .single();

    if (error) throw error;

    // Invia email di conferma al cliente
    await sendServiceRequestEmailToClient(request, service, provider);
    
    // Invia email di notifica all'admin/provider
    await sendServiceRequestEmailToProvider(request, service, provider);

    // Aggiorna flag email inviata
    await supabase
      .from('service_requests')
      .update({ email_sent: true })
      .eq('id', request.id);

    return { success: true, request };
  } catch (error) {
    console.error('Errore creazione richiesta servizio:', error);
    return { success: false, error: (error as Error).message };
  }
};

// Ottieni tutte le richieste (per admin)
export const getAllServiceRequests = async (
  status?: string,
  page: number = 1,
  limit: number = 20
): Promise<{ 
  success: boolean; 
  requests?: ServiceRequest[]; 
  total?: number; 
  error?: string 
}> => {
  try {
    let query = supabase
      .from('service_requests')
      .select(`
        *,
        service:service_id (
          id,
          title_it,
          title_en,
          category,
          duration_minutes,
          price_per_person,
          price_per_group
        ),
        provider:provider_id (
          id,
          name,
          email,
          phone,
          whatsapp,
          specialization
        )
      `, { count: 'exact' });

    if (status && status !== 'all') {
      query = query.eq('status', status);
    }

    const from = (page - 1) * limit;
    const to = from + limit - 1;

    const { data, error, count } = await query
      .range(from, to)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return { success: true, requests: data || [], total: count || 0 };
  } catch (error) {
    console.error('Errore recupero richieste servizi:', error);
    return { success: false, error: (error as Error).message };
  }
};

// Aggiorna status richiesta
export const updateServiceRequestStatus = async (
  requestId: string,
  status: string,
  notes?: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const updateData: any = {
      status,
      updated_at: new Date().toISOString()
    };

    if (notes) {
      updateData.admin_notes = notes;
    }

    if (status === 'contacted') {
      updateData.response_sent_at = new Date().toISOString();
    }

    const { error } = await supabase
      .from('service_requests')
      .update(updateData)
      .eq('id', requestId);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Errore aggiornamento status richiesta:', error);
    return { success: false, error: (error as Error).message };
  }
};

// ===== FUNZIONI EMAIL =====

// Invia email di conferma al cliente
const sendServiceRequestEmailToClient = async (
  request: any,
  service: Service,
  provider: ServiceProvider
): Promise<void> => {
  const subject = `Conferma richiesta: ${service.title_it}`;
  
  const htmlContent = `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
      <h2 style="color: #0d9488;">Grazie per la tua richiesta!</h2>
      
      <p>Ciao <strong>${request.client_name}</strong>,</p>
      
      <p>Abbiamo ricevuto la tua richiesta per il servizio <strong>${service.title_it}</strong>.</p>
      
      <div style="background: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
        <h3 style="margin-top: 0; color: #0d9488;">Dettagli della richiesta:</h3>
        <p><strong>Servizio:</strong> ${service.title_it}</p>
        <p><strong>Provider:</strong> ${provider.name}</p>
        ${request.requested_date ? `<p><strong>Data richiesta:</strong> ${request.requested_date}</p>` : ''}
        ${request.requested_time ? `<p><strong>Orario richiesto:</strong> ${request.requested_time}</p>` : ''}
        <p><strong>Partecipanti:</strong> ${request.participants_count}</p>
        ${request.message ? `<p><strong>Messaggio:</strong> ${request.message}</p>` : ''}
      </div>
      
      <p>Ti contatteremo entro <strong>24 ore</strong> per confermare i dettagli e organizzare il servizio.</p>
      
      ${provider.whatsapp ? `
        <div style="background: #d1fae5; padding: 15px; border-radius: 8px; margin: 20px 0;">
          <p style="margin: 0;"><strong>Per urgenze, puoi contattare direttamente ${provider.name} su WhatsApp:</strong></p>
          <p style="margin: 5px 0 0 0;">
            <a href="https://wa.me/${provider.whatsapp.replace('+', '')}" 
               style="color: #059669; text-decoration: none;">
              📱 ${provider.whatsapp}
            </a>
          </p>
        </div>
      ` : ''}
      
      <p>Grazie per aver scelto J-O-H!</p>
      
      <div style="border-top: 1px solid #e5e7eb; padding-top: 20px; margin-top: 30px; color: #6b7280; font-size: 14px;">
        <p>J-O-H - Servizi Premium<br>
        Email: info@j-o-h.com<br>
        Sito web: https://j-o-h.com</p>
      </div>
    </div>
  `;

  const textContent = `Grazie ${request.client_name}! Abbiamo ricevuto la tua richiesta per ${service.title_it}. Ti contatteremo entro 24 ore.`;

  const template = {
    subject,
    html: htmlContent,
    text: textContent
  };

  await sendEmail(request.client_email, template);
};

// Invia email di notifica al provider/admin
const sendServiceRequestEmailToProvider = async (
  request: any,
  service: Service,
  provider: ServiceProvider
): Promise<void> => {
  const subject = `🔔 Nuova richiesta servizio: ${service.title_it}`;
  
  const htmlContent = `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
      <h2 style="color: #dc2626;">Nuova Richiesta Servizio</h2>
      
      <div style="background: #fee2e2; padding: 20px; border-radius: 8px; margin: 20px 0;">
        <h3 style="margin-top: 0; color: #dc2626;">⚠️ AZIONE RICHIESTA</h3>
        <p>È arrivata una nuova richiesta per il servizio <strong>${service.title_it}</strong></p>
        <p><strong>Provider:</strong> ${provider.name}</p>
      </div>
      
      <div style="background: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
        <h3 style="margin-top: 0; color: #0d9488;">Dettagli Cliente:</h3>
        <p><strong>Nome:</strong> ${request.client_name}</p>
        <p><strong>Email:</strong> ${request.client_email}</p>
        ${request.client_phone ? `<p><strong>Telefono:</strong> ${request.client_phone}</p>` : ''}
        
        <h4 style="color: #0d9488; margin-top: 20px;">Dettagli Richiesta:</h4>
        ${request.requested_date ? `<p><strong>Data richiesta:</strong> ${request.requested_date}</p>` : ''}
        ${request.requested_time ? `<p><strong>Orario richiesto:</strong> ${request.requested_time}</p>` : ''}
        <p><strong>Partecipanti:</strong> ${request.participants_count}</p>
        ${request.message ? `<p><strong>Messaggio cliente:</strong><br>"${request.message}"</p>` : ''}
      </div>
      
      <div style="background: #dbeafe; padding: 20px; border-radius: 8px; margin: 20px 0;">
        <h3 style="margin-top: 0; color: #1e40af;">📋 Prossimi Passi:</h3>
        <ol>
          <li>Contatta il cliente entro 24 ore</li>
          <li>Conferma disponibilità e dettagli</li>
          <li>Aggiorna lo status nella dashboard admin</li>
        </ol>
      </div>
      
      <p><strong>ID Richiesta:</strong> ${request.id}</p>
      <p><strong>Data richiesta:</strong> ${new Date(request.created_at).toLocaleDateString('it-IT')}</p>
      
      <div style="border-top: 1px solid #e5e7eb; padding-top: 20px; margin-top: 30px; color: #6b7280; font-size: 14px;">
        <p>Questa email è stata generata automaticamente dal sistema J-O-H.</p>
      </div>
    </div>
  `;

  const textContent = `Nuova richiesta servizio da ${request.client_name} per ${service.title_it}. Controlla la dashboard admin per i dettagli.`;

  const template = {
    subject,
    html: htmlContent,
    text: textContent
  };

  // Invia email al provider
  await sendEmail(provider.email, template);
  
  // Invia email all'admin (separata)
  const adminTemplate = {
    ...template,
    subject: `[ADMIN] ${subject}`
  };
  await sendEmail('admin@j-o-h.com', adminTemplate);
};

// Ottieni statistiche servizi per dashboard admin
export const getServiceStats = async (): Promise<{ 
  success: boolean; 
  stats?: any; 
  error?: string 
}> => {
  try {
    // Conta richieste per status
    const { data: statusStats, error: statusError } = await supabase
      .from('service_requests')
      .select('status')
      .then(response => {
        if (response.error) throw response.error;
        
        const stats = response.data.reduce((acc: any, item: any) => {
          acc[item.status] = (acc[item.status] || 0) + 1;
          return acc;
        }, {});
        
        return { data: stats, error: null };
      });

    if (statusError) throw statusError;

    // Conta servizi per categoria
    const { data: categoryStats, error: categoryError } = await supabase
      .from('services')
      .select('category')
      .eq('status', 'active')
      .then(response => {
        if (response.error) throw response.error;
        
        const stats = response.data.reduce((acc: any, item: any) => {
          acc[item.category] = (acc[item.category] || 0) + 1;
          return acc;
        }, {});
        
        return { data: stats, error: null };
      });

    if (categoryError) throw categoryError;

    return {
      success: true,
      stats: {
        requestsByStatus: statusStats || {},
        servicesByCategory: categoryStats || {}
      }
    };
  } catch (error) {
    console.error('Errore recupero statistiche:', error);
    return { success: false, error: (error as Error).message };
  }
}; 